import { useState, lazy, Suspense, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAtomValue } from 'jotai';
import { alpha, keyframes } from '@mui/material/styles';
import { brandGradient } from '@/lib/themeHelpers';
import { isAuthenticatedAtom } from '@/store/authAtoms';
import { useConfig, isYes, useAriaName } from '@/hooks/useConfig';
import { useContent } from '@/hooks/useContent';
import { interpolateContent } from '@/lib/content';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import useMediaQuery from '@mui/material/useMediaQuery';
import Face3Icon from '@mui/icons-material/Face3';
import CloseIcon from '@mui/icons-material/Close';

const DashboardAI = lazy(() => import('@/pages/DashboardAI'));

// Subtle attention pulse â€” keeps the entry point discoverable without being noisy.
const pulse = keyframes`
  0%   { box-shadow: 0 6px 18px rgba(0,0,0,0.2), 0 0 0 0 rgba(161,6,147,0.45); }
  70%  { box-shadow: 0 6px 18px rgba(0,0,0,0.2), 0 0 0 12px rgba(161,6,147,0); }
  100% { box-shadow: 0 6px 18px rgba(0,0,0,0.2), 0 0 0 0 rgba(161,6,147,0); }
`;

// Paths where the FAB should be suppressed (chat already on screen, or pre-auth flows).
const HIDDEN_ROUTES = new Set(['/dashboard-ai', '/login', '/sign-in', '/register', '/account-recovery', '/home', '/facility-selection']);

const FAB_SIZE = 60;
const DRAG_THRESHOLD = 6;

// Stored as right/bottom distances from viewport edges — stable across window resizes.
interface FabPos { right: number; bottom: number }
interface DragState { startX: number; startY: number; startRight: number; startBottom: number; moved: boolean }

function readPersistedPos(): FabPos | null {
  try {
    const raw = localStorage.getItem('ariaFabPos');
    if (raw) return JSON.parse(raw) as FabPos;
  } catch { /* ignore */ }
  return null;
}

export function AriaFab() {
  const { getContentString } = useContent();
  const location = useLocation();
  const isAuthed = useAtomValue(isAuthenticatedAtom);
  const ariaName = useAriaName();
  const dashboardAiEnabled = isYes(useConfig('PPDashboardAIEnabled'));
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [open, setOpen] = useState(false);
  // null → CSS default positioning. non-null → explicit right/bottom px from viewport edges.
  const [pos, setPos] = useState<FabPos | null>(readPersistedPos);
  const [isDragging, setIsDragging] = useState(false);
  // Drag bookkeeping in a ref to avoid stale-closure issues — never triggers re-renders mid-drag.
  const drag = useRef<DragState | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    drag.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRight: window.innerWidth - rect.right,
      startBottom: window.innerHeight - rect.bottom,
      moved: false,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
      d.moved = true;
      setIsDragging(true);
    }
    if (!d.moved) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    setPos({
      right:  Math.max(8, Math.min(w - FAB_SIZE - 8, d.startRight  - dx)),
      bottom: Math.max(8, Math.min(h - FAB_SIZE - 8, d.startBottom - dy)),
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    const d = drag.current;
    if (!d) return;
    const { moved } = d;
    drag.current = null;
    setIsDragging(false);
    if (moved) {
      setPos(prev => {
        if (prev) {
          try { localStorage.setItem('ariaFabPos', JSON.stringify(prev)); } catch { /* ignore */ }
        }
        return prev;
      });
    } else {
      setOpen(true);
    }
  }, []);

  // ── All hooks above; early returns below ─────────────────────────────────────
  if (!isAuthed || !dashboardAiEnabled) return null;
  if (HIDDEN_ROUTES.has(location.pathname)) return null;

  const drawerWidth = isMobile ? '100vw' : 420;
  // Shift up on mobile so we clear the bottom nav (â‰ˆ56px + safe area).
  // Shift up on desktop so we clear the footer banner (â‰ˆ44px) with breathing room.
  const fabBottom = isMobile ? 'calc(72px + env(safe-area-inset-bottom, 0px))' : 80;
  const ariaLabel = interpolateContent(getContentString('PPNavDashboardAI'), { name: ariaName });

  return (
    <>
      {!open && (
        <Tooltip title={isDragging ? '' : ariaLabel} placement="left">
          <Fab
            aria-label={ariaLabel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            sx={(theme) => ({
              position: 'fixed',
              // Custom dragged position uses explicit right/bottom. Default uses CSS values
              // that include insetInlineEnd to handle RTL layouts automatically.
              ...(pos
                ? { bottom: pos.bottom, right: pos.right }
                : { bottom: fabBottom, insetInlineEnd: 24 }),
              zIndex: theme.zIndex.speedDial,
              width: FAB_SIZE,
              height: FAB_SIZE,
              color: 'common.white',
              background: brandGradient(theme),
              opacity: isDragging ? 0.9 : 0.55,
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
              touchAction: 'none',
              animation: isDragging ? 'none' : `${pulse} 2.4s ease-out 1.5s 2`,
              '&:hover': {
                background: brandGradient(theme),
                transform: isDragging ? 'none' : 'scale(1.06)',
                opacity: 1,
              },
              transition: isDragging ? 'none' : 'transform 150ms ease, opacity 150ms ease',
            })}
          >
            <Face3Icon sx={{ fontSize: 30 }} />
          </Fab>
        </Tooltip>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: false }}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Drawer header */}
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.25,
            background: brandGradient(theme),
            color: 'common.white',
            flexShrink: 0,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: alpha('#fff', 0.2), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Face3Icon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
              {ariaLabel}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOpen(false)}
            aria-label={getContentString('PortalClose')}
            size="small"
            sx={{ color: 'common.white', '&:hover': { bgcolor: alpha('#fff', 0.15) } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Chat body â€” fills remaining drawer height */}
        <Box sx={{ flex: 1, minHeight: 0, p: 1.5, overflow: 'hidden' }}>
          <Suspense fallback={<Skeleton variant="rounded" height="100%" sx={{ borderRadius: 3 }} />}>
            <DashboardAI embedded onNavigate={() => setOpen(false)} />
          </Suspense>
        </Box>
      </Drawer>
    </>
  );
}
